"use client";

import { AudioComprehensionPostView } from '@/app/(public)/learning-feed/AudioComprehensionPostView';
import { getFeedSpeechAssetPathAsUrl } from '@/lib/feed/assets';
import { LearningFeedPost } from '@/lib/types/feed';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AudioPlayer from "react-h5-audio-player";
import H5AudioPlayer from 'react-h5-audio-player';
import { motion } from 'framer-motion';
import { PauseIcon, PlayIcon } from '@heroicons/react/24/outline';

export function AudioComprehensionPlayer({ post }: { post: LearningFeedPost }) {
    const [isDragging, setIsDragging] = useState(false);
    const [containerWidth, setContainerWidth] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const audioPlayerRef = useRef<H5AudioPlayer>(null);

    // Audio event handlers
    const handlePlay = useCallback(() => {
        setIsPlaying(true);
    }, []);

    const handlePause = useCallback(() => {
        setIsPlaying(false);
    }, []);

    const handleListen = useCallback(() => {
        if (audioPlayerRef.current?.audio?.current) {
            setCurrentTime(audioPlayerRef.current.audio.current.currentTime);
        }
    }, []);

    const handleLoadedMetaData = useCallback(() => {
        if (audioPlayerRef.current?.audio?.current) {
            setDuration(audioPlayerRef.current.audio.current.duration);
        }
    }, []);

    const handleEnded = useCallback(() => {
        setIsPlaying(false);
        setCurrentTime(0);

        // Rewind the audio to the start
        if (audioPlayerRef.current?.audio?.current) {
            audioPlayerRef.current.audio.current.currentTime = 0;
        }        
    }, []);

    const handlePlayPause = useCallback(() => {
        if (audioPlayerRef.current?.audio?.current) {
            const audio = audioPlayerRef.current.audio.current;
            if (isPlaying) {
                audio.pause();
            } else {
                audio.play();
            }                    
        }
    }, [isPlaying]);

    // Calculate progress percentage
    const progressPercentage = useMemo(() => {
        if (duration === 0) return 0;
        return (currentTime / duration) * 100;
    }, [currentTime, duration]);

    // Update container width on mount and resize
    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
            }
        };
        
        updateWidth();
        (typeof window !== "undefined" && (window as any))?.addEventListener('resize', updateWidth);
        return () => (typeof window !== "undefined" && (window as any))?.removeEventListener('resize', updateWidth);
    }, []);

    return <div ref={containerRef} className="relative w-full">
        <div className="w-full h-full flex items-center justify-center px-0 py-4 pt-2">
            {/* Custom Audio Player Facade */}
            <div className="flex items-center space-x-6 w-full max-w-md">
                {/* Play/Pause Button */}
                <motion.button
                    onClick={handlePlayPause}
                    className="flex-shrink-0 w-16 h-16 bg-blue-500 hover:bg-blue-600 rounded-full flex items-center justify-center text-white shadow-lg transition-colors duration-200"
                    whileHover={{ scale: 0.9 }}
                    whileTap={{ scale: 0.85 }}
                    animate={{
                        backgroundColor: isPlaying ? "#3b82f6" : "#1d4ed8",
                        boxShadow: isPlaying 
                            ? ["0px 0px 15px -5px rgba(159, 130, 246, 0.75)", "0px 0px 15px -5px rgba(59, 130, 246, 0.8)", "0px 0px 15px -5px rgba(59, 130, 246, 0.75)"]
                            : "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
                        scale: isPlaying ? 0.9 : 1
                    }}
                    transition={{ 
                        duration: 0.2,
                        boxShadow: {
                            duration: 1.5,
                            repeat: isPlaying ? Infinity : 0,
                            ease: "easeInOut"
                        }
                    }}
                >
                    <motion.div
                        initial={false}
                        animate={{ 
                            rotate: isPlaying ? 0 : 0,
                            scale: isPlaying ? 1 : 1
                        }}
                        transition={{ duration: 0.2 }}
                    >
                        {isPlaying ? (
                            <PauseIcon className="w-8 h-8" />
                        ) : (
                            <PlayIcon className="w-8 h-8 ml-1" />
                        )}
                    </motion.div>
                </motion.button>

                {/* Progress Bar */}
                <div className="flex-1">
                    <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                        <motion.div
                            className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full"
                            initial={{ width: "0%" }}
                            animate={{ 
                                width: `${progressPercentage}%`,
                                opacity: isPlaying ? 1 : 0.8
                            }}
                            transition={{ 
                                width: { duration: 0.1, ease: "linear" },
                                opacity: { duration: 0.2 }
                            }}
                        />                                                                
                    </div>
                </div>
            </div>
        </div>

        <div className="hidden">        
            {post.audioUrl && <AudioPlayer
                src={getFeedSpeechAssetPathAsUrl(post.audioUrl)}
                autoPlayAfterSrcChange={false}
                autoPlay={false}
                crossOrigin='anonymous'
                ref={audioPlayerRef}
                onPlay={handlePlay}
                onPause={handlePause}
                onListen={handleListen}
                onLoadedMetaData={handleLoadedMetaData}
                onEnded={handleEnded}
                listenInterval={100}
            />}
        </div>
    </div>
}

export default AudioComprehensionPlayer;